import {Form, Formik} from 'formik';
import {isEmpty, isFinite, pickBy} from 'lodash-es';

import {AfriexTransactionFeeType} from '@afriex/core/domain/enums';

import {Button, Input, Modal, Select} from '@/components';
import {NumberInput} from '@/components/common/Input';
import type {FeeTransactionTypes, TransactionFee} from '@/types';
import {
  currencyPairOptions,
  FeeTransactionChannels,
  limitTypeOptions,
  paymentMethodOptions,
} from '@/types';
import {getFeeTransactionTypesOptions} from '@/types/AfriexFees';
import {logger} from '@/utils/logger';

import {overrideFeeSchema} from './validation';

/**
 * Formik's `setFieldValue` returns a promise that settles after the
 * re-validation it may trigger. All eight `<Select>` / `<NumberInput>` handlers
 * below are the same shape: a synchronous change handler with nowhere to put a
 * promise, so each was leaving that promise floating. They therefore all take
 * the same `.catch(logger.error)`.
 *
 * This form sets `validateOnChange={false}`, so nothing revalidates on these
 * calls and the promise cannot reject in practice. The dev logger rather than a
 * toast, because these calls only move a value into form state: the actual save
 * is `handleSave` below, which revalidates the whole override and reports its
 * own failure to the admin.
 */
const FeeEntryModal = ({
  isOpen,
  onClose,
  fee,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (fee?: TransactionFee) => void;
  fee?: Partial<TransactionFee>;
}) => {
  const handleSave = (values: Partial<TransactionFee>) => {
    const {
      baseValue: fixedValue,
      percentValue: percentage,
      maxFeeValue: maxFee,
    } = values;

    // Format currency pair correctly - if both are '*' (any), set to undefined
    let formattedCurrencyPair = values?.currencyPair;

    // Check if both source and destination are set to "*" (or empty)
    const [source, destination] = (values?.currencyPair || ':').split(':');
    if (
      (isEmpty(source) || source === '*') &&
      (isEmpty(destination) || destination === '*')
    ) {
      formattedCurrencyPair = undefined;
    }

    const newOverride: TransactionFee = {
      ...(fee ?? {}),
      paymentMethod: values?.paymentMethod,
      baseValue: isFinite(fixedValue) ? Number(fixedValue) : undefined,
      percentValue: isFinite(percentage) ? Number(percentage) : undefined,
      maxFeeValue: isFinite(maxFee) ? Number(maxFee) : undefined,
      limitType: values?.limitType,
      currencyPair: formattedCurrencyPair,
      limit: values?.limit,
      type: AfriexTransactionFeeType.OVERRIDE,
      transactionType: values?.transactionType,
    };
    const cleanOverride = pickBy(
      newOverride,
      (value) => isFinite(value) || !isEmpty(value),
    );
    try {
      // revalidate incase form was modified
      overrideFeeSchema.validateSync(cleanOverride);
    } catch (e: unknown) {
      logger.log((e as Error).message);
      alert(
        'Error validating fee override. Please check the form and try again.',
      );
      return;
    }
    onSave(cleanOverride as TransactionFee);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title="Fee Entry"
    >
      <Formik
        initialValues={{
          type: 'override',
          paymentMethod: '',
          currencyPair: '',
          baseValue: '',
          percentValue: '',
          limitType: '',
          limit: '',
          transactionType: '',
          ...(fee ?? {}),
        }}
        validationSchema={overrideFeeSchema}
        onSubmit={(values) => {
          return handleSave(values as Partial<TransactionFee>);
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({values, errors, handleChange, setFieldValue}) => {
          const methodOrCurrencyError =
            errors.paymentMethod || errors.currencyPair;

          // Parse the currency pair or default to "*:*"
          const currencyPairStr = values.currencyPair || '*:*';
          const [source, destination] = currencyPairStr.split(':');

          const sourceCurrency = source || '*';
          const destinationCurrency = destination || '*';

          return (
            <Form className="flex flex-col gap-4 mb-4">
              <div className="flex flex-col items-center justify-between gap-3">
                <div className="">
                  <p
                    className={`${errors.transactionType ? 'text-red-500' : 'text-gray-500'} text-sm`}
                  >
                    {errors.transactionType}
                  </p>
                </div>

                <div className="flex items-center justify-between gap-3 w-full">
                  <Select
                    name="transactionType"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedType =
                        e.target.value === 'any' ? '' : e.target.value;
                      setFieldValue('transactionType', selectedType).catch(
                        logger.error,
                      );
                    }}
                    value={
                      values.transactionType === ('' as FeeTransactionTypes)
                        ? 'any'
                        : values.transactionType
                    }
                    options={getFeeTransactionTypesOptions}
                    label="Transaction Type:"
                    className="w-1/2"
                  />

                  <Select
                    name="paymentMethod"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedMethod: FeeTransactionChannels | '' =
                        e.target.value === 'any'
                          ? ''
                          : (
                              Object.create(FeeTransactionChannels) as Record<
                                string,
                                FeeTransactionChannels
                              >
                            )[e.target.value];
                      setFieldValue('paymentMethod', selectedMethod).catch(
                        logger.error,
                      );
                    }}
                    value={
                      values.paymentMethod === '' ? 'any' : values.paymentMethod
                    } // Map empty string to "any" for display purposes
                    options={paymentMethodOptions}
                    label="Payment Method:"
                    className="w-1/2"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-between gap-3">
                <div className="flex flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
                    Currency Pair:
                  </label>

                  <div className="flex flex-row items-center gap-2">
                    <Select
                      name="sourceCurrency"
                      value={sourceCurrency}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const newSource = e.target.value;
                        const newPair = `${newSource}:${destinationCurrency}`;

                        setFieldValue('currencyPair', newPair).catch(
                          logger.error,
                        );
                      }}
                      options={currencyPairOptions}
                      className="w-1/2"
                    />

                    <Select
                      name="destinationCurrency"
                      value={destinationCurrency}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const newDestination = e.target.value;
                        const newPair = `${sourceCurrency}:${newDestination}`;

                        setFieldValue('currencyPair', newPair).catch(
                          logger.error,
                        );
                      }}
                      options={currencyPairOptions}
                      className="w-1/2"
                    />
                  </div>

                  <div className="">
                    <p
                      className={`${methodOrCurrencyError ? 'text-red-500' : 'text-gray-500'} text-sm`}
                    >
                      {methodOrCurrencyError}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <Select
                    name="limitType"
                    value={values.limitType === '' ? 'none' : values.limitType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedValue =
                        e.target.value === 'none' ? '' : e.target.value;
                      setFieldValue('limitType', selectedValue).catch(
                        logger.error,
                      );
                    }}
                    options={limitTypeOptions}
                    label="Limit Type:"
                    className="w-2/3"
                  />

                  <Input
                    type="number"
                    min={0}
                    name="limit"
                    value={values.limit}
                    disabled={isEmpty(values.limitType)}
                    required={!isEmpty(values.limitType)}
                    onChange={handleChange}
                    error={errors.limit}
                    label="Limit Value ($):"
                    className="w-full"
                  />
                </div>
              </div>

              <p className="text-gray-500 dark:text-gray-200 text-sm">
                <span className="font-medium">PS: </span>When limits are set,
                the limit value fee, will be applied only if the limit on
                transaction amount in the USD is met.
              </p>
              <hr className="col-span-2 dark:border-dark-borderMid" />

              <div className="flex flex-col gap-3">
                <NumberInput
                  min={0}
                  name="baseValue"
                  value={values.baseValue}
                  onValueChange={(rawValue: string) => {
                    setFieldValue('baseValue', Number(rawValue)).catch(
                      logger.error,
                    );
                  }}
                  error={errors.baseValue}
                  label={`Fixed (${destinationCurrency ?? '$'}):`}
                  className="w-full"
                />

                <NumberInput
                  min={0}
                  name="maxFeeValue"
                  value={values.maxFeeValue}
                  onValueChange={(rawValue: string) => {
                    setFieldValue('maxFeeValue', Number(rawValue)).catch(
                      logger.error,
                    );
                  }}
                  error={errors.maxFeeValue}
                  label={`Max Fee ("$"):`}
                  className="w-full"
                />

                <NumberInput
                  max={5}
                  name="percentValue"
                  value={values.percentValue}
                  onValueChange={(rawValue: string) => {
                    setFieldValue('percentValue', Number(rawValue)).catch(
                      logger.error,
                    );
                  }}
                  error={errors.percentValue}
                  label="Percentage (%):"
                  className="w-full"
                />
              </div>

              <p className="text-gray-500 text-sm dark:text-gray-300">
                Either fixed amount or percentage be entered or both. <br />
                Formular of Fee:
                <span className="mx-1">fixed + (amount * percent / 100)</span>
                <br />
                <span className="font-medium">NB: </span>
                Fixed fee currency is source currency unless destination
                currency is specified.
              </p>
              <hr className="col-span-2 dark:border-dark-borderMid" />

              <div className="flex justify-end">
                <Button colorScheme="cyan" type="submit" className="h-10">
                  Save
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default FeeEntryModal;
